import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { adminApi, tradingApi, ledgerApi, TradingStatus, LedgerSummary } from '../services/api'
import { StatCard, Loader } from '../components/UI'
import { formatToman, formatDateTimeFa, toFaDigits } from '../utils/format'

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<any>(null)
  const [prices, setPrices] = useState<any>(null)
  const [trading, setTrading] = useState<TradingStatus | null>(null)
  const [ledger, setLedger] = useState<LedgerSummary | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([
      adminApi.dashboard(),
      adminApi.currentPrices().catch(() => null),
      tradingApi.getStatus().catch(() => null),
      ledgerApi.get().catch(() => null)
    ])
      .then(([d, p, t, l]) => {
        if (d.data.success) setStats(d.data.data)
        setPrices(p?.data?.data || null)
        if (t?.data?.success) setTrading(t.data.data || null)
        if (l?.data?.success) setLedger(l.data.data?.summary || null)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <div className="py-20 flex justify-center"><Loader /></div>

  const positive = ledger?.position === 'POSITIVE'

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-black text-gray-900">داشبورد</h1>
        <p className="text-sm text-gray-500 mt-1">نمای کلی فروشگاه طلا و نقره</p>
      </div>

      {trading?.anyStopped && (
        <div className="card p-4 border-2 border-red-300 bg-red-50 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-red-800">
            <b>معاملات در حالت استاپ:</b>
            {trading.sellStopped && <span className="ml-3">فروش متوقف (قیمت‌ها ستاره‌دار)</span>}
            {trading.buyStopped && <span className="ml-3">خرید متوقف (پنل فروش بسته)</span>}
          </div>
          <Link to="/trading" className="btn btn-danger !py-1.5 !text-xs">مدیریت استاپ</Link>
        </div>
      )}

      {/* تراز لحظه‌ای */}
      {ledger && (
        <Link
          to="/ledger"
          className={`card p-5 border-2 block hover:shadow-md transition-shadow ${
            positive ? 'border-green-300 bg-green-50' : 'border-gold-300 bg-gold-50'
          }`}
        >
          <div className="flex flex-wrap items-center justify-between gap-5">
            <div>
              <div className="text-xs text-gray-500 mb-1">تراز لحظه‌ای (برای جزئیات کلیک کنید)</div>
              <div className={`text-2xl font-black ${positive ? 'text-green-700' : 'text-gold-800'}`}>
                {formatToman(Math.abs(ledger.balance))}
              </div>
              <div className={`mt-1.5 inline-block px-3 py-0.5 rounded-full text-xs font-black ${
                positive ? 'bg-green-600 text-white' : 'bg-gold-600 text-white'
              }`}>
                {positive ? 'مثبت — پول بیشتری دست شماست' : 'منفی — فلز بیشتری دست شماست'}
              </div>
            </div>
            <div className="flex flex-wrap gap-6 text-sm">
              <div>
                <span className="text-gray-500 text-xs block">نقد</span>
                <b className={ledger.cashNet >= 0 ? 'text-green-700' : 'text-red-600'}>
                  {formatToman(ledger.cashNet)}
                </b>
              </div>
              <div>
                <span className="text-gray-500 text-xs block">موجودی فلز</span>
                <b>{toFaDigits(ledger.netGrams)} گرم</b>
              </div>
              <div>
                <span className="text-gray-500 text-xs block">ارزش روز فلز</span>
                <b>{formatToman(ledger.metalValue)}</b>
              </div>
              <div>
                <span className="text-gray-500 text-xs block">سفارش‌های خرید / فروش</span>
                <b>{toFaDigits(ledger.buyOrderCount)} / {toFaDigits(ledger.sellOrderCount)}</b>
              </div>
            </div>
          </div>
        </Link>
      )}

      {prices && !prices.priceHidden && (
        <div className="card p-4 flex flex-wrap items-center gap-4 justify-between">
          <span className="text-xs font-bold text-gray-500">قیمت خام فعلی (منبع: {prices.source})</span>
          <div className="flex flex-wrap gap-6 text-sm">
            <div><span className="text-gray-500 text-xs block">طلا ۲۴ عیار</span><b>{formatToman(prices.gold24KPerGram)}</b></div>
            <div><span className="text-gray-500 text-xs block">طلا ۱۸ عیار</span><b>{formatToman(prices.gold18KPerGram)}</b></div>
            <div><span className="text-gray-500 text-xs block">نقره خام</span><b>{formatToman(prices.silverRawPerGram)}</b></div>
          </div>
          <span className="text-[11px] text-gray-400">{formatDateTimeFa(prices.updatedAt)}</span>
        </div>
      )}

      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard title="کاربران" value={toFaDigits(stats?.totalUsers || 0)} icon="👥" />
        <StatCard title="محصولات فعال" value={toFaDigits(stats?.totalProducts || 0)} icon="🛍️" color="bg-gold-50 text-gold-700" />
        <StatCard title="کل سفارش‌ها" value={toFaDigits(stats?.totalOrders || 0)} icon="🧾" color="bg-blue-50 text-blue-700" />
        <StatCard title="سفارش‌های در انتظار" value={toFaDigits(stats?.pendingOrders || 0)} icon="⏳" color="bg-yellow-50 text-yellow-700" />
        <StatCard title="درآمد تکمیل‌شده" value={formatToman(stats?.totalRevenue || 0)} icon="💵" color="bg-green-50 text-green-700" />
      </div>

      {stats?.recentOrders && stats.recentOrders.length > 0 && (
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex justify-between items-center">
            <h2 className="font-black text-gray-900">آخرین سفارش‌ها</h2>
            <div className="flex gap-3">
              <Link to="/trades" className="text-sm text-primary-600 font-bold">خرید و فروش ←</Link>
              <Link to="/orders" className="text-sm text-primary-600 font-bold">همه سفارش‌ها ←</Link>
            </div>
          </div>
          <div className="table-wrap">
            <table className="table-admin">
              <thead>
                <tr>
                  <th>شماره سفارش</th>
                  <th>مشتری</th>
                  <th>نوع</th>
                  <th className="!text-center">اقلام</th>
                  <th>مبلغ</th>
                  <th>وضعیت</th>
                  <th>تاریخ</th>
                </tr>
              </thead>
              <tbody>
                {stats.recentOrders.map((o: any) => (
                  <tr key={o.id}>
                    <td className="font-bold" dir="ltr">{o.orderNumber}</td>
                    <td>{o.user?.firstName} {o.user?.lastName}</td>
                    <td>
                      <span className={`badge ${o.type === 'BUY' ? 'bg-green-100 text-green-800' : 'bg-gold-100 text-gold-800'}`}>
                        {o.type === 'BUY' ? 'خرید مشتری' : 'فروش مشتری'}
                      </span>
                    </td>
                    <td className="text-center">{toFaDigits(o.items?.length || 0)}</td>
                    <td className="font-bold">{formatToman(o.total)}</td>
                    <td>
                      <span className="badge bg-yellow-100 text-yellow-800">{o.status}</span>
                    </td>
                    <td className="text-xs text-gray-500">{formatDateTimeFa(o.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

export default Dashboard
